import React, { useMemo, useState } from 'react';

// useState restored for Private toggle gate
import { EditInput } from '@/components/shared/Inputs';
import {
	SettingsList,
	SettingsListItems,
} from '@/components/shared/SettingCard';
import { SingleActionLayout } from '@/components/shared/SingleActionLayout/SingleActionLayout';
// import { RoundedButton } from '@/components/shared/RoundedButton'; // Change Art — pending artSeed persistence on backend (CTO)
import { SongThemeChip } from '@/components/shared/SongThemeChip';
import { AsaphSwitch } from '@/components/shared/Switch';
import { UpgradeGateSheet } from '@/components/shared/UpgradeGateSheet';
import { UserMilestones } from '@/config/constants';
import { TeamSong, useCreateTeamSongMutation } from '@/graphql';
import { generateAlbumGradient } from '@/helpers/albumGradient';
import { getAcronym } from '@/helpers/textTransform';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { FormikErrors, FormikHandlers, FormikValues, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';

import {
	AddCustomSongDisplayProps,
	EAddCustomSongDisplayState,
} from '../AddCustomSong';
import styles from './AddCustomSongRoot.module.scss';

export type FirstStepFormSchema = {
	name: string;
	artist: string;
	ccli?: number;
};

export const firstStepValidationSchema: Yup.SchemaOf<FirstStepFormSchema> =
	Yup.object().shape({
		name: Yup.string()
			.required('Name is required')
			.max(80, 'Max 80 characters'),
		artist: Yup.string()
			.required('Artist is required')
			.max(80, 'Max 80 characters'),
		ccli: Yup.number().optional(),
	});

type ExtendOnChangeFn = (
	e: React.ChangeEvent<any>,
	field: keyof FirstStepFormSchema,
) => void;

export const FirstStep: React.FC<{
	formik: FormikHandlers & {
		values: FormikValues;
		errors: FormikErrors<FirstStepFormSchema>;
	};
	setTeamSong?: (
		stateFunction: (state: Partial<TeamSong>) => Partial<TeamSong>,
	) => void;
	additionalItems: SettingsListItems;
}> = ({ formik, setTeamSong, additionalItems }) => {
	const extendOnChange: ExtendOnChangeFn = (e, field) => {
		formik.handleChange(e);
		if (setTeamSong) {
			setTeamSong((state) => ({
				...state,
				[field]: e.target.value,
			}));
		}
	};

	return (
		<form
			onSubmit={formik.handleSubmit}
			id="asaphSignIn"
			className={styles['song-modal-root']}
		>
			<div className={styles['centered-items']}>
				<div className={styles['cover-image']}>
					<span>{getAcronym(formik.values.name)}</span>
				</div>
				<EditInput
					className={styles['name']}
					placeholder="Enter song name"
					name="name"
					autoComplete="off"
					value={formik.values.name}
					onChange={(e) => extendOnChange(e, 'name')}
					error={!!formik.errors.name}
					// errorText={formik.errors.name}
					helperText="Song name*"
				/>
				<EditInput
					className={styles['artist']}
					placeholder="Enter artist name"
					name="artist"
					autoComplete="off"
					size="md"
					value={formik.values.artist}
					onChange={(e) => extendOnChange(e, 'artist')}
					error={!!formik.errors.artist}
					// errorText={formik.errors.artist}
					helperText="Artist*"
				/>
				<EditInput
					className={styles['ccli']}
					placeholder="Enter CCLI #"
					name="ccli"
					autoComplete="off"
					size="md"
					value={formik.values.ccli}
					onChange={(e) => {
						formik.handleChange(e);
						if (setTeamSong) {
							setTeamSong((state) => ({
								...state,
								ccli: parseInt(e.target.value),
							}));
						}
					}}
					error={!!formik.errors.ccli}
					helperText="CCLI #"
				/>
			</div>
			<SettingsList items={additionalItems} noChevron />
		</form>
	);
};

type ValidatedTeamSong = Omit<
	AddCustomSongDisplayProps['teamSong'],
	keyof FirstStepFormSchema
> &
	FirstStepFormSchema;

export const AddCustomSongRoot: React.FC<AddCustomSongDisplayProps> = ({
	teamSong,
	setTeamSong,
	pushDisplayState,
	popDisplayState,
	setOpen,
	handleCompleteClose,
}) => {
	const { enqueueSnackbar } = useSnackbar();
	const { name, artist, ccli } = teamSong;
	const { appendMilestone, hasMilestones } = useAuth();
	const { teamSongs } = useTeamSongs();
	const { mutateAsync, isLoading } = useCreateTeamSongMutation();
	const { refetch } = useTeamSongs();

	const fireSaveEvent = useAnalyticsEvent('Add_Custom_Song_Save');
	const fireErrorEvent = useAnalyticsEvent('Error');

	const [isPrivate, setIsPrivate] = useState(false);
	const [showPrivateGate, setShowPrivateGate] = useState(false);

	const initialValues: FirstStepFormSchema = useMemo(
		() => ({
			name: name || '',
			artist: artist || '',
			ccli: ccli || undefined,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const { handleSubmit, isValid, values, handleChange, errors } = useFormik({
		initialValues,
		validationSchema: firstStepValidationSchema,
		onSubmit: async () => {
			const validatedTeamSong = teamSong as ValidatedTeamSong;
			try {
				await mutateAsync({
					input: validatedTeamSong,
				});
				enqueueSnackbar('Successfully added song', {
					variant: 'success',
				});
				refetch();
				fireSaveEvent({
					songJson: JSON.stringify(validatedTeamSong),
				});
				handleCompleteClose();
			} catch (err) {
				enqueueSnackbar('Something went wrong while adding song', {
					variant: 'warning',
				});
				fireErrorEvent({
					name: 'AddCustomSongError',
					details: err,
				});
			}
		},
	});

	// const [artIndex, setArtIndex] = useState(0); // Change Art — pending artSeed persistence on backend (CTO)

	const extendOnChange: ExtendOnChangeFn = (e, field) => {
		handleChange(e);
		if (setTeamSong) {
			setTeamSong((state) => ({
				...state,
				[field]: e.target.value,
			}));
		}
	};

	// const albumGradient = generateAlbumGradient('art-' + artIndex); // Change Art — restore when artSeed persists
	const albumGradient = generateAlbumGradient('art-0');

	const additionalItems: SettingsListItems = [
		{
			type: 'title',
			title: 'Song info',
		},
		{
			type: 'setting',
			title: 'Song Lyrics',
			subtitle: (() => {
				const firstLine = teamSong.lyrics
					?.split('\n')
					.find((line) => line.trim().length > 0);
				return firstLine ? `${firstLine}…` : 'No lyrics added';
			})(),
			onClick: () => {
				pushDisplayState(EAddCustomSongDisplayState.LYRICS);
			},
		},
		{ type: 'title', title: 'Tags' },
		{
			type: 'setting',
			title: 'Energy',
			trailingComponent: teamSong.energyTags?.length ? (
				<div
					style={{
						display: 'flex',
						gap: '4px',
						flexWrap: 'wrap',
						justifyContent: 'flex-end',
					}}
				>
					{teamSong.energyTags.map((tag) => (
						<SongThemeChip key={tag} themeTag={tag} />
					))}
				</div>
			) : undefined,
			onClick: () =>
				pushDisplayState(EAddCustomSongDisplayState.ENERGY_TAG),
		},
		{
			type: 'setting',
			title: 'Song Type',
			trailingComponent: teamSong.songTypeTags?.length ? (
				<div
					style={{
						display: 'flex',
						gap: '4px',
						flexWrap: 'wrap',
						justifyContent: 'flex-end',
					}}
				>
					{teamSong.songTypeTags.map((tag) => (
						<SongThemeChip key={tag} themeTag={tag} />
					))}
				</div>
			) : undefined,
			onClick: () =>
				pushDisplayState(EAddCustomSongDisplayState.SONG_TYPE_TAG),
		},
		{
			type: 'title',
			title: 'Visibility',
		},
		{
			type: 'setting',
			title: 'Private',
			noChevron: true,
			trailingComponent: (
				<AsaphSwitch
					checked={isPrivate}
					onChange={(_e) => {
						if (!isPrivate) {
							// toggling ON — show gate
							setShowPrivateGate(true);
						} else {
							// toggling OFF — always allowed
							setIsPrivate(false);
						}
					}}
				/>
			),
		},
		{
			type: 'title',
			title: 'Listen',
		},
		{
			type: 'setting',
			title: 'Spotify URL',
			onClick: () => {
				pushDisplayState(EAddCustomSongDisplayState.SPOTIFY_URL);
			},
			trailingText: teamSong.spotifyId,
		},
		{
			type: 'setting',
			title: 'YouTube URL',
			onClick: () => {
				pushDisplayState(EAddCustomSongDisplayState.YOUTUBE_URL);
			},
			trailingText: teamSong.youtubeId,
		},
		{
			type: 'setting',
			title: 'Apple Music URL',
			onClick: () => {
				pushDisplayState(EAddCustomSongDisplayState.APPLE_MUSIC_URL);
			},
			trailingText: teamSong.appleMusicId,
		},
		{
			type: 'setting',
			title: 'Recorded key',
			onClick: () => {
				pushDisplayState(EAddCustomSongDisplayState.RECORDED_KEY);
			},
			trailingComponent: teamSong.recordedKey ? (
				<SongThemeChip themeTag={teamSong.recordedKey} />
			) : undefined,
		},
	];

	return (
		<SingleActionLayout
			actionLabel="Save song"
			actionDisabled={!isValid || isLoading}
			actionDivider
			actionClassName={styles['next-button']}
			onActionClick={handleSubmit}
		>
			<form
				onSubmit={handleSubmit}
				id="asaphSignIn"
				className={styles['song-modal-root']}
			>
				<div className={styles['centered-items']}>
					<div
						className={styles['cover-image']}
						style={albumGradient.style}
					>
						<span style={{ color: albumGradient.textColor }}>
							{getAcronym(values.name)}
						</span>
					</div>
					{/* Change Art — pending artSeed persistence on backend (CTO)
					<RoundedButton
						size="md"
						color="default"
						onClick={() => setArtIndex((i) => i + 1)}
					>
						Change Art
					</RoundedButton>
					*/}
					<EditInput
						className={styles['name']}
						placeholder="Enter song name"
						name="name"
						autoComplete="off"
						value={values.name}
						onChange={(e) => extendOnChange(e, 'name')}
						error={!!errors.name}
						// errorText={errors.name}
						helperText="Song name*"
					/>
					<EditInput
						className={styles['artist']}
						placeholder="Enter artist name"
						name="artist"
						autoComplete="off"
						size="md"
						value={values.artist}
						onChange={(e) => extendOnChange(e, 'artist')}
						error={!!errors.artist}
						// errorText={errors.artist}
						helperText="Artist*"
					/>
					<EditInput
						className={styles['ccli']}
						placeholder="Enter CCLI #"
						name="ccli"
						autoComplete="off"
						size="md"
						value={values.ccli}
						onChange={(e) => {
							handleChange(e);
							if (setTeamSong) {
								setTeamSong((state) => ({
									...state,
									ccli: parseInt(e.target.value),
								}));
							}
						}}
						error={!!errors.ccli}
						helperText="CCLI #"
					/>
				</div>
				<SettingsList items={additionalItems} noChevron />
			</form>
			<UpgradeGateSheet
				open={showPrivateGate}
				onClose={() => setShowPrivateGate(false)}
				heading="Private songs"
				body="Private songs are a paid feature. Continue to keep this one for your team only."
				gateName="private_songs"
				onSkip={() => setIsPrivate(true)}
				rememberAcceptance={true}
			/>
		</SingleActionLayout>
	);
};
